import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { CONSTS } from '../const';
import { useLocation } from 'react-router-dom';
import useAuthTokens from '../utils/hooks/useAuthTokens';
import { logger } from '../utils/logger';
import { getCsrfTokenFromCookies } from '../utils/csrf';
const AuthContext = createContext({
  isAuthenticated: false,
  isLoading: true,
  checkAuthStatus: () => {},
  setIsAuthenticated: () => {},
  redirectToLogin: () => {},
});

export const redirectToLogin = () => {
  window.location.href = `${process.env.REACT_APP_LOGIN_URL}/login`;
};

const refreshToken = async (callback) => {
  try {
    const { csrfTokenKey, csrfToken } = getCsrfTokenFromCookies();
    const response = await axios.post(
      `${process.env.REACT_APP_ONBOARDING_API_ENDPOINT}/graphql`,
      {
        query: `query Refresh {
          refreshAccessToken {
            status
            statusCode
            errorCode
            message
            data { accessToken }
          }
        }`,
        variables: {},
      },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          [csrfTokenKey]: csrfToken,
        },
      }
    );
    logger.debug('Refresh token response:', response);
    const refresh = response?.data?.data?.refreshAccessToken;
    if (refresh?.status == CONSTS.OK) {
      const accessToken = refresh?.data?.accessToken;
      if (accessToken) {
        localStorage.setItem('accessToken', accessToken);
        if (callback) {
          callback();
        }
        return true;
      }
    }
  } catch (error) {
    checkForRedirect(error?.status);
    logger.error('Error refreshing token:', error);
  }
};
export const checkForRedirect = async (statusCode) => {
  if (statusCode == CONSTS.UNAUTHORIZED_STATUS_CODE) {
    redirectToLogin();
  } else if (statusCode == CONSTS.EXPIRED_STATUS_CODE) {
    return await refreshToken();
  }
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isSessionChecked, setIsSessionChecked] = useState(false);
  const { accessToken } = useAuthTokens();
  const [isLoading, setIsLoading] = useState(true);

  const checkAuthStatus = async (isRetryAfterTokenRefresh = false) => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      const { csrfTokenKey, csrfToken } = getCsrfTokenFromCookies();
      const response = await axios.post(
        `${process.env.REACT_APP_ONBOARDING_API_ENDPOINT}/graphql`,
        {
          query: `query CheckSession {
            checkSession {
              status
              statusCode
              errorCode
              message
            }
          }`,
          variables: {},
        },
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
            [csrfTokenKey]: csrfToken,
          },
        }
      );

      logger.log('response', response);

      const checkSession = response?.data?.data?.checkSession;
      const gqlErrors = response?.data?.errors;
      const gqlStatusCode = gqlErrors?.[0]?.extensions?.statusCode;
      // The backend may signal auth failures either via GraphQL `errors[]`
      // or inside the `checkSession` payload itself. Treat both the same.
      const effectiveStatusCode = gqlStatusCode ?? checkSession?.statusCode;

      if (effectiveStatusCode == CONSTS.EXPIRED_STATUS_CODE) {
        if (!isRetryAfterTokenRefresh) {
          const refreshed = await refreshToken();
          if (refreshed) {
            await checkAuthStatus(true);
            return;
          }
        }
        setIsAuthenticated(false);
        redirectToLogin();
        return;
      }
      if (effectiveStatusCode == CONSTS.UNAUTHORIZED_STATUS_CODE) {
        setIsAuthenticated(false);
        redirectToLogin();
        return;
      }

      if (checkSession?.status === CONSTS.OK) {
        // setUser(response.data.user);
        setIsAuthenticated(true);
      } else {
        // Unknown failure (no recognizable status code) - send the user
        // back to login instead of rendering a blank screen.
        setIsAuthenticated(false);
        redirectToLogin();
      }
    } catch (error) {
      const status = error?.response?.status ?? error?.status;
      if (status == CONSTS.EXPIRED_STATUS_CODE) {
        if (!isRetryAfterTokenRefresh) {
          const refreshed = await refreshToken();
          if (refreshed) {
            await checkAuthStatus(true);
            return;
          }
        }
        setIsAuthenticated(false);
      } else if (status == CONSTS.UNAUTHORIZED_STATUS_CODE) {
        setIsAuthenticated(false);
        redirectToLogin();
      } else {
        // Network/unknown error - avoid leaving the user on a blank screen.
        setIsAuthenticated(false);
        redirectToLogin();
      }
    } finally {
      setIsLoading(false);
      setIsSessionChecked(true);
    }
  };
  const location = useLocation();
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const encodedData = urlParams.get('data');

    try {
      if (encodedData) {
        const decodedData = JSON.parse(atob(encodedData));
        if (decodedData.accessToken) {
          localStorage.setItem('accessToken', decodedData.accessToken);
          logger.log('accessToken', decodedData.accessToken);
          // Clean up the URL by removing the data parameter
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          );
        }
      }
    } catch (error) {
      logger.error('Error processing URL data:', error);
    } finally {
      checkAuthStatus();
    }
  }, []);

  // useEffect(() => {
  //   if (!isAuthenticated && isSessionChecked) {
  //     // redirectToLogin();
  //   }
  // }, [isAuthenticated, isSessionChecked]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        redirectToLogin,
        // user,
        checkAuthStatus,
        setIsAuthenticated,
      }}
    >
      {isSessionChecked && isAuthenticated ? children : null}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
